import {
  DIAGRAM_ARROW_MAX_POINTS,
  DIAGRAM_MAX_STEP,
  DIAGRAM_MULTI_STEP_KINDS,
  type DiagramArrowKind,
  type DiagramElement,
  type DiagramPitch,
  type DiagramZoneColor,
  type DiagramZoneRotation,
  type DiagramZoneShape,
} from '@ukinfo/shared';
import { arrowMiddle, clamp, toCoordinate, type FieldPoint } from './diagramEdit';
import { PITCH_HEIGHT } from './PitchDiagram';

/**
 * Reine Rechenhilfen fuer Knickpfeile, Zonen (Form, Farbe, Groesse) und die Blickrichtung des Skizzeneditors -
 * ohne React, damit sie sich einzeln pruefen lassen.
 *
 * Feldkoordinaten wie in `diagramEdit.ts`: x und y in Prozent des Spielfelds, ganze Zahlen. Alle Funktionen
 * liefern neue Elemente und lassen die Eingabe unveraendert; gibt es nichts zu aendern, kommt dasselbe Element zurueck.
 */

type Arrow = Extract<DiagramElement, { type: 'arrow' }>;
type Zone = Extract<DiagramElement, { type: 'zone' }>;
type Player = Extract<DiagramElement, { type: 'player' }>;
type Vertex = [number, number];

/* ------------------------------------------------------------------ Knickpunkte */

/** Eckpunkte des Pfeils von Anfang bis Ende: Anfang, Knickpunkte, Ende (eine Kurve zaehlt als Strecke Anfang-Ende). */
function arrowVertices(element: Arrow): Vertex[] {
  return [element.from, ...(element.points ?? []), element.to];
}

/** Hat ein Pfeil dieser Art Platz fuer einen weiteren Knickpunkt? (Der hohe Ball hat keine, sonst hoechstens 8.) */
export function canAddKnick(element: Arrow): boolean {
  return element.kind !== 'LOFT' && (element.points?.length ?? 0) < DIAGRAM_ARROW_MAX_POINTS;
}

/** Pfeil mit neuen Knickpunkten; eine Kurve (`via`) entfaellt dabei, ohne Knickpunkte entfallen `points` und `smooth`. */
function withKnicks(element: Arrow, points: Vertex[]): Arrow {
  const { via: _via, points: _points, smooth: _smooth, ...rest } = element;
  if (points.length === 0) return rest;
  return element.smooth ? { ...rest, points, smooth: true } : { ...rest, points };
}

/** Pfeilarten, die mit mehreren Punkten gezeichnet standardmaessig weich sind: Dribbling und Laufweg (Pass, Schuss und Wechsel sind eckig). */
export function smoothByDefault(kind: DiagramArrowKind): boolean {
  return kind === 'DRIBBLE' || kind === 'RUN';
}

/**
 * Verlaengert den Pfeil um einen Punkt: das bisherige Ende wird zum letzten Knickpunkt, `point` das neue Ende.
 * Der erste Knickpunkt macht Dribbling und Laufweg weich (`smoothByDefault`), spaeter bleibt es, wie es ist.
 * `null`, wenn der Pfeil keine Knickpunkte mehr aufnimmt (hoher Ball, 8 Stueck).
 */
export function extendArrow(element: Arrow, point: FieldPoint): Arrow | null {
  if (!canAddKnick(element)) return null;
  const first = (element.points?.length ?? 0) === 0;
  const base = withKnicks(first && smoothByDefault(element.kind) ? { ...element, smooth: true } : element, [...(element.points ?? []), element.to]);
  return { ...base, to: [point.x, point.y] };
}

/** Der Pfeil mit Knickpunkten als weiche Kurve (`true`) oder als eckiger Linienzug (`false`); ohne Knickpunkte bleibt er, wie er ist. */
export function setSmooth(element: Arrow, smooth: boolean): Arrow {
  if (!element.points || (element.smooth === true) === smooth) return element;
  const { smooth: _alt, ...rest } = element;
  return smooth ? { ...rest, smooth: true } : rest;
}

/** Darf der Pfeil dieser Art ueber mehrere Schritte gehen ("Laeuft bis Schritt")? Laufweg, Dribbling und Positionswechsel. */
export function canSpanSteps(kind: DiagramArrowKind): boolean {
  return (DIAGRAM_MULTI_STEP_KINDS as readonly string[]).includes(kind);
}

/**
 * Der Pfeil mit neuer Schrittnummer (`undefined` = automatisch). "Laeuft bis Schritt" (`stepEnd`) zieht mit: ohne Schrittnummer entfaellt es
 * (es gaebe keinen Startschritt); liegt der neue Startschritt nicht mehr vor dem Endschritt, wandert der Endschritt mit und behaelt die
 * Spanne (ueber die hoechste Schrittnummer hinaus entfaellt er); sonst bleibt er.
 */
export function withStep(element: Arrow, step: number | undefined): Arrow {
  const { step: oldStep, stepEnd, ...rest } = element;
  if (step === undefined) return rest;
  if (stepEnd === undefined) return { ...rest, step };
  if (stepEnd > step) return { ...rest, step, stepEnd };
  const moved = step + (stepEnd - (oldStep ?? stepEnd - 1));
  return moved <= DIAGRAM_MAX_STEP && moved > step ? { ...rest, step, stepEnd: moved } : { ...rest, step };
}

/** Der Pfeil laeuft bis Schritt `end` (`undefined` = nur sein Schritt). Ohne Schrittnummer, bei einem Endschritt nicht nach dem Startschritt oder bei einer Art ohne Spanne bleibt er, wie er ist. */
export function withStepEnd(element: Arrow, end: number | undefined): Arrow {
  const { stepEnd: _alt, ...rest } = element;
  if (end === undefined) return _alt === undefined ? element : rest;
  if (!canSpanSteps(element.kind) || element.step === undefined || end <= element.step || end > DIAGRAM_MAX_STEP) return element;
  return { ...rest, stepEnd: end };
}

/** Abstand eines Punktes von der Strecke a-b in Zeichnungseinheiten. */
function distanceToSegment(point: FieldPoint, a: Vertex, b: Vertex, pitch: DiagramPitch): number {
  const scale = PITCH_HEIGHT[pitch] / 100;
  const px = point.x;
  const py = point.y * scale;
  const ax = a[0];
  const ay = a[1] * scale;
  const bx = b[0];
  const by = b[1] * scale;
  const lengthSquared = (bx - ax) ** 2 + (by - ay) ** 2;
  const t = lengthSquared === 0 ? 0 : clamp(((px - ax) * (bx - ax) + (py - ay) * (by - ay)) / lengthSquared, 0, 1);
  return Math.hypot(px - (ax + (bx - ax) * t), py - (ay + (by - ay) * t));
}

/**
 * Setzt einen Knickpunkt an `point` in die Strecke des Pfeils, die diesem Punkt am naechsten liegt (Tipp auf ein Segment).
 * Aus einer Kurve wird dabei ein Linienzug mit diesem einen Knick. `null` ohne Platz oder wenn der Punkt auf einer Ecke liegt.
 */
export function insertKnick(element: Arrow, point: FieldPoint, pitch: DiagramPitch): Arrow | null {
  if (!canAddKnick(element)) return null;
  const vertices = arrowVertices(element);
  if (vertices.some((vertex) => vertex[0] === point.x && vertex[1] === point.y)) return null;
  let best = 0;
  let bestDistance = Infinity;
  for (let index = 0; index < vertices.length - 1; index += 1) {
    const gap = distanceToSegment(point, vertices[index]!, vertices[index + 1]!, pitch);
    if (gap < bestDistance) {
      best = index;
      bestDistance = gap;
    }
  }
  const points = [...(element.points ?? [])];
  points.splice(best, 0, [point.x, point.y]);
  return withKnicks(element, points);
}

/**
 * Knopf "Knick hinzufuegen": ein Knickpunkt in der Mitte der laengsten Strecke (in Zeichnungseinheiten gemessen).
 * Bei einer Kurve ist es die Mitte der Kurve - sie wird zum Linienzug mit einem Knick. `null` ohne Platz.
 */
export function addKnickAtLongest(element: Arrow, pitch: DiagramPitch): Arrow | null {
  if (!canAddKnick(element)) return null;
  if (element.via) {
    const middle = arrowMiddle(element);
    return withKnicks(element, [[toCoordinate(middle.x), toCoordinate(middle.y)]]);
  }
  const vertices = arrowVertices(element);
  let best = 0;
  let bestLength = -1;
  for (let index = 0; index < vertices.length - 1; index += 1) {
    const a = vertices[index]!;
    const b = vertices[index + 1]!;
    const length = Math.hypot(b[0] - a[0], ((b[1] - a[1]) * PITCH_HEIGHT[pitch]) / 100);
    if (length > bestLength) {
      best = index;
      bestLength = length;
    }
  }
  const a = vertices[best]!;
  const b = vertices[best + 1]!;
  const points = [...(element.points ?? [])];
  points.splice(best, 0, [toCoordinate((a[0] + b[0]) / 2), toCoordinate((a[1] + b[1]) / 2)]);
  return withKnicks(element, points);
}

/** Entfernt den Knickpunkt `index` (ohne Index den letzten); der Pfeil bleibt ein Linienzug oder wird wieder gerade. */
export function removeKnick(element: Arrow, index?: number): Arrow {
  const points = [...(element.points ?? [])];
  if (points.length === 0) return element;
  points.splice(index === undefined || index < 0 || index >= points.length ? points.length - 1 : index, 1);
  return withKnicks(element, points);
}

/** Setzt den Knickpunkt `index` an einen neuen Ort. */
export function moveKnick(element: Arrow, index: number, point: FieldPoint): Arrow {
  const current = element.points?.[index];
  if (!element.points || !current || (current[0] === point.x && current[1] === point.y)) return element;
  return { ...element, points: element.points.map((knick, at): Vertex => (at === index ? [point.x, point.y] : knick)) };
}

/* ------------------------------------------------------------------ Zonen */

/** Kleinste Seitenlaenge einer Zone in Feldprozent - auch beim Zeichnen: schmalere Zonen gibt es nicht. */
export const ZONE_MIN_SIZE = 2;

export type ZoneCorner = 'nw' | 'ne' | 'sw' | 'se';
export const ZONE_CORNERS: readonly ZoneCorner[] = ['nw', 'ne', 'sw', 'se'];

/** Ort einer Ecke der Zone in Feldkoordinaten. */
export function zoneCornerPoint(zone: Zone, corner: ZoneCorner): FieldPoint {
  return { x: corner.endsWith('w') ? zone.x : zone.x + zone.w, y: corner.startsWith('n') ? zone.y : zone.y + zone.h };
}

/**
 * Zieht eine Ecke der Zone an `point`; die gegenueberliegende Ecke bleibt stehen. Die Zone behaelt mindestens
 * `ZONE_MIN_SIZE` auf jeder Achse (die Ecke springt nicht ueber die Gegenecke) und bleibt im Feld.
 */
export function resizeZone(zone: Zone, corner: ZoneCorner, point: FieldPoint): Zone {
  const fixedX = corner.endsWith('w') ? zone.x + zone.w : zone.x;
  const fixedY = corner.startsWith('n') ? zone.y + zone.h : zone.y;
  const x = corner.endsWith('w') ? Math.min(point.x, fixedX - ZONE_MIN_SIZE) : Math.max(point.x, fixedX + ZONE_MIN_SIZE);
  const y = corner.startsWith('n') ? Math.min(point.y, fixedY - ZONE_MIN_SIZE) : Math.max(point.y, fixedY + ZONE_MIN_SIZE);
  const left = toCoordinate(Math.min(x, fixedX));
  const top = toCoordinate(Math.min(y, fixedY));
  const width = toCoordinate(Math.max(x, fixedX)) - left;
  const height = toCoordinate(Math.max(y, fixedY)) - top;
  if (left === zone.x && top === zone.y && width === zone.w && height === zone.h) return zone;
  return { ...zone, x: left, y: top, w: width, h: height };
}

/** Form, Farbe und Drehung einer Zone beim Zeichnen oder im Editor; Standardwerte (Rechteck, Weiss, 0) werden nicht gespeichert. */
export interface ZoneLook {
  shape: DiagramZoneShape;
  color: DiagramZoneColor;
  rotation: DiagramZoneRotation;
}

export const DEFAULT_ZONE_LOOK: ZoneLook = { shape: 'rect', color: 'white', rotation: 0 };

/** Die Zone mit dieser Form und Farbe; was dem Standard entspricht, entfaellt (die Drehung gilt nur beim Dreieck). */
export function applyZoneLook(zone: Zone, look: ZoneLook): Zone {
  const { shape: _shape, color: _color, rotation: _rotation, ...rest } = zone;
  return {
    ...rest,
    ...(look.shape !== 'rect' ? { shape: look.shape } : {}),
    ...(look.color !== 'white' ? { color: look.color } : {}),
    ...(look.shape === 'triangle' && look.rotation !== 0 ? { rotation: look.rotation } : {}),
  };
}

/** Form, Farbe und Drehung, die eine Zone zeigt (mit Standardwerten fuer fehlende Felder). */
export function zoneLookOf(zone: Zone): ZoneLook {
  return { shape: zone.shape ?? 'rect', color: zone.color ?? 'white', rotation: zone.rotation ?? 0 };
}

/* ------------------------------------------------------------------ Blickrichtung */

/** Die acht Richtungen der Knoepfe: Winkel in Grad (0 = oben, im Uhrzeigersinn), Name und Pfeilzeichen. */
export const FACING_DIRECTIONS: ReadonlyArray<{ angle: number; label: string; glyph: string }> = [
  { angle: 0, label: 'oben', glyph: '↑' },
  { angle: 45, label: 'oben rechts', glyph: '↗' },
  { angle: 90, label: 'rechts', glyph: '→' },
  { angle: 135, label: 'unten rechts', glyph: '↘' },
  { angle: 180, label: 'unten', glyph: '↓' },
  { angle: 225, label: 'unten links', glyph: '↙' },
  { angle: 270, label: 'links', glyph: '←' },
  { angle: 315, label: 'oben links', glyph: '↖' },
];

/** Raster des Einrastens beim Drehen am Griff. */
export const FACING_SNAP_DEGREES = 45;

/** Winkel auf 0 bis 359 gebracht und auf ganze Grad gerundet. */
export function normalizeFacing(degrees: number): number {
  return ((Math.round(degrees) % 360) + 360) % 360;
}

/**
 * Blickrichtung (Grad, 0 = oben, im Uhrzeigersinn) von `center` zu `point`, in Zeichnungseinheiten gemessen.
 * Mit `snapStep` auf dieses Raster gerundet. `null`, wenn der Punkt (fast) auf der Mitte liegt - dann gibt es keine Richtung.
 */
export function facingFromPoint(center: FieldPoint, point: FieldPoint, pitch: DiagramPitch, snapStep: number | null): number | null {
  const dx = point.x - center.x;
  const dy = ((point.y - center.y) * PITCH_HEIGHT[pitch]) / 100;
  if (Math.hypot(dx, dy) < 1.5) return null;
  const degrees = (Math.atan2(dx, -dy) * 180) / Math.PI;
  return normalizeFacing(snapStep ? Math.round(degrees / snapStep) * snapStep : degrees);
}

/** Der Spieler mit dieser Blickrichtung; `null` entfernt sie. */
export function withFacing(element: Player, facing: number | null): Player {
  const { facing: _alt, ...rest } = element;
  return facing === null ? rest : { ...rest, facing: normalizeFacing(facing) };
}

/** Setzt die Blickrichtung aller Spieler der Auswahl (andere Elemente bleiben); `null` entfernt sie. Dieselbe Liste, wenn sich nichts aendert. */
export function setFacing(elements: readonly DiagramElement[], indices: readonly number[], facing: number | null): DiagramElement[] {
  const target = facing === null ? null : normalizeFacing(facing);
  let changed = false;
  const next = elements.map((element, index) => {
    if (!indices.includes(index) || element.type !== 'player' || (element.facing ?? null) === target) return element;
    changed = true;
    return withFacing(element, target);
  });
  return changed ? next : (elements as DiagramElement[]);
}
